import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Navbar from "./components/Navbar";
import IcNameBanner from "./components/IcNameBanner";
import IcNameModal from "./components/IcNameModal";
import ReviewPromptModal from "./components/ReviewPromptModal";
import ChatWidget from "./components/ChatWidget";
import BanScreen from "./components/BanScreen";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

const Home = lazy(() => import("./pages/Home"));
const Fleet = lazy(() => import("./pages/Fleet"));
const Profile = lazy(() => import("./pages/Profile"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const LandlordPanel = lazy(() => import("./pages/LandlordPanel"));
const RentalsPanel = lazy(() => import("./pages/RentalsPanel"));
const Rules = lazy(() => import("./pages/Rules"));
const LoginCallback = lazy(() => import("./pages/LoginCallback"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EmployeePortal = lazy(() => import("./pages/EmployeePortal"));
const Reminders = lazy(() => import("./pages/Reminders"));
const BookingCalendar = lazy(() => import("./pages/BookingCalendar"));
const CarConfigurator = lazy(() => import("./pages/CarConfigurator"));
const CarSuggestions = lazy(() => import("./pages/CarSuggestions"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const Events = lazy(() => import("./pages/Events"));
const GiftCards = lazy(() => import("./pages/GiftCards"));
const AdminChat = lazy(() => import("./pages/AdminChat"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Challenges = lazy(() => import("./pages/Challenges"));
const Garage = lazy(() => import("./pages/Garage"));
const DamageReports = lazy(() => import("./pages/DamageReports"));
const PointsShop = lazy(() => import("./pages/PointsShop"));
const Lottery = lazy(() => import("./pages/Lottery"));
const LandlordProfile = lazy(() => import("./pages/LandlordProfile"));
const WheelSpin = lazy(() => import("./pages/WheelSpin"));
const SlotMachine = lazy(() => import("./pages/SlotMachine"));
const Favorites = lazy(() => import("./pages/Favorites"));
const BlackjackPage = lazy(() => import("./pages/Blackjack"));
const SeemtaV4 = lazy(() => import("./pages/SeemtaV4"));
const PromoterPage = lazy(() => import("./pages/PromoterPage"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <div className="min-h-screen font-sans antialiased text-white bg-black">
        <div className="app-background" />
        <div className="app-overlay" />
        <BanScreen />
        <BrowserRouter>
          <Navbar />
          <IcNameBanner />
          <IcNameModal />
          <ReviewPromptModal />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/rules" element={<Rules />} />
              <Route path="/fleet" element={<Fleet />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/landlord" element={<LandlordPanel />} />
              <Route path="/rentals" element={<RentalsPanel />} />
              <Route path="/employees" element={<EmployeePortal />} />
              <Route path="/reminders" element={<Reminders />} />
              <Route path="/calendar" element={<BookingCalendar />} />
              <Route path="/configurator" element={<CarConfigurator />} />
              <Route path="/suggestions" element={<CarSuggestions />} />
              <Route path="/user/:userId" element={<PublicProfile />} />
              <Route path="/events" element={<Events />} />
              <Route path="/giftcards" element={<GiftCards />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/challenges" element={<Challenges />} />
              <Route path="/garage" element={<Garage />} />
              <Route path="/damage-reports" element={<DamageReports />} />
              <Route path="/shop" element={<PointsShop />} />
              <Route path="/lottery" element={<Lottery />} />
              <Route path="/wheel" element={<WheelSpin />} />
              <Route path="/slots" element={<SlotMachine />} />
              <Route path="/blackjack" element={<BlackjackPage />} />
              <Route path="/seemta-v4" element={<SeemtaV4 />} />
              <Route path="/promoter" element={<PromoterPage />} />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/landlord/:id" element={<LandlordProfile />} />
              <Route path="/admin/chat" element={<AdminChat />} />
              <Route path="/auth/callback" element={<LoginCallback />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ChatWidget />
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
